/**
 * Estimates this device's clock offset from the server, so the metronome's
 * opt-in sync mode can put two devices on the same downbeat even when their
 * system clocks disagree.
 *
 * State is module-level on purpose: the offset describes the device, not any
 * one component, so the dock and the public page share a single measurement.
 */

type ClockStatus = 'idle' | 'syncing' | 'ready' | 'degraded'

interface ClockSample {
  offset: number
  rtt: number
}

/** Samples per handshake. More would buy little; the best-of filter does the work. */
const SAMPLE_COUNT = 5

/** Re-measure if the last handshake is older than this. */
const STALE_AFTER_MS = 5 * 60 * 1000

const offsetMs = ref(0)
const accuracyMs = ref(0)
const status = ref<ClockStatus>('idle')

let lastSyncedAt = 0
let inFlight: Promise<void> | null = null

async function takeSample(): Promise<ClockSample | null> {
  try {
    const sent = Date.now()
    const response = await $fetch<{ now: number }>('/api/time')
    const received = Date.now()

    if (!response || typeof response.now !== 'number') return null

    // Standard NTP offset: compare the server's clock against the midpoint of
    // our own send/receive pair, which cancels out symmetric network latency.
    return {
      offset: response.now - (sent + received) / 2,
      rtt: received - sent,
    }
  } catch {
    return null
  }
}

async function measure(): Promise<void> {
  status.value = 'syncing'
  const samples: ClockSample[] = []

  for (let i = 0; i < SAMPLE_COUNT; i++) {
    const sample = await takeSample()
    if (sample) samples.push(sample)
  }

  if (!samples.length) {
    // Fail soft: fall back to the raw device clock rather than refusing to
    // start. Sync still works between devices whose clocks happen to agree,
    // which is exactly what the old fixed-epoch implementation did.
    offsetMs.value = 0
    accuracyMs.value = 0
    status.value = 'degraded'
    return
  }

  // Keep the fastest round trip rather than averaging. Averaging lets a single
  // cold start (which on the Netlify preset can be ~1s) drag the estimate off
  // by hundreds of milliseconds; the lowest-RTT sample is the one whose
  // symmetric-latency assumption is least wrong.
  const best = samples.reduce((a, b) => (b.rtt < a.rtt ? b : a), samples[0]!)

  offsetMs.value = best.offset
  accuracyMs.value = Math.round(best.rtt / 2)
  lastSyncedAt = Date.now()
  status.value = 'ready'
}

export function useServerClock() {
  /** Measure once, then reuse until stale. Concurrent callers share one handshake. */
  async function ensureSynced(force = false): Promise<void> {
    if (!import.meta.client) return
    if (inFlight) return inFlight

    const fresh = status.value === 'ready' && Date.now() - lastSyncedAt < STALE_AFTER_MS
    if (fresh && !force) return

    inFlight = measure().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  /** Wall-clock milliseconds, corrected toward the server. */
  function syncedNow(): number {
    return Date.now() + offsetMs.value
  }

  return {
    offsetMs: readonly(offsetMs),
    accuracyMs: readonly(accuracyMs),
    status: readonly(status),
    ensureSynced,
    syncedNow,
  }
}
